import { DATA_GRID_MODE } from '@/constants/dataGrid';
import {
  DataGrid as MuiDataGrid,
  type DataGridProps,
  type GridPaginationModel,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { formatPaginationDisplayedRows } from '@/lib/formatPaginationDisplayedRows';
import { Pagination } from '@/primitives/Pagination';

export { GridActionsCellItem, type GridColDef } from '@mui/x-data-grid-pro';

export function DataGrid<T extends GridValidRowModel>(
  props: DataGridProps<T> & {
    /** Hide the built-in footer and render a floating pagination pill instead. */
    paginationPill?: boolean;
  },
) {
  const {
    paginationPill = false,
    sx: sxProp,
    slotProps: slotPropsProp,
    ...rest
  } = props;

  const slotProps = {
    ...slotPropsProp,
    pagination: {
      ...slotPropsProp?.pagination,
      labelDisplayedRows:
        (
          slotPropsProp?.pagination as
            | { labelDisplayedRows?: typeof formatPaginationDisplayedRows }
            | undefined
        )?.labelDisplayedRows ?? formatPaginationDisplayedRows,
    },
  } as DataGridProps<T>['slotProps'];

  const handlePaginationModelChange = rest.onPaginationModelChange as
    | ((model: GridPaginationModel) => void)
    | undefined;

  const pillPage = rest.paginationModel?.page ?? 0;
  const pillPageSize = rest.paginationModel?.pageSize ?? 10;
  const pillRowCount = rest.rowCount ?? rest.rows?.length ?? 0;
  const pillPageCount =
    pillPageSize > 0 ? Math.max(1, Math.ceil(pillRowCount / pillPageSize)) : 1;
  const pillPageSizeOptions = rest.pageSizeOptions
    ?.map((option) => (typeof option === 'number' ? option : option.value))
    .filter((option) => Number.isFinite(option));

  const grid = (
    <MuiDataGrid
      getRowClassName={(params) =>
        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
      }
      paginationMode={DATA_GRID_MODE.SERVER}
      slotProps={slotProps}
      {...rest}
      hideFooter={paginationPill || rest.hideFooter}
      sx={[
        (theme) => ({
          '&.MuiDataGrid-root': {
            borderWidth: '0px',
            minHeight: '200px',
            bgcolor: 'background.paper',

            '.MuiDataGrid-overlayWrapper': {
              minHeight: '200px',
            },
          },
          '.MuiDataGrid-topContainer::after': {
            backgroundColor: 'unset',
          },
          '.MuiDataGrid-columnHeader': {
            color: 'text.primary',
            bgcolor: 'background.paper',
          },
          '.MuiDataGrid-columnHeaderTitleContainerContent': {
            fontSize: '14px',
            fontWeight: '500',
            letterSpacing: '0.1px',
            color: 'text.primary',
          },
          '.MuiDataGrid-row.even .MuiDataGrid-cell--pinnedRight': {
            bgcolor: 'background.default',
          },
          '.MuiDataGrid-row.odd .MuiDataGrid-cell--pinnedRight': {
            bgcolor: 'background.paper',
          },
          '.MuiDataGrid-cell': {
            borderWidth: '0px',
            // Typography variant=bodysmall
            fontSize: '14px',
            fontWeight: '400',
            letterSpacing: '0.25px',
          },
          '.MuiDataGrid-footerContainer': {
            borderWidth: '0px',
            bgcolor: 'background.paper',
          },
          '.even': {
            bgcolor: 'background.default',
          },
          '.odd': {
            bgcolor: 'background.paper',
          },
          '.MuiTablePagination-root': {
            color: 'text.secondary',

            '.MuiTablePagination-select': {
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'primary.main',
              borderRadius: '999px',
              padding: '8px',
              paddingRight: '32px !important',
              color: 'primary.main',
              transition: 'none',
            },
            '.MuiTablePagination-input svg': {
              color: 'primary.main',
              right: '8px',
            },
            '.MuiTablePagination-actions .MuiButtonBase-root:not(.Mui-disabled)':
              {
                color: 'primary.main',
                backgroundColor: theme.vars
                  ? `rgba(${theme.vars.palette.primary.mainChannel} / 0.08)`
                  : alpha(theme.palette.primary.main, 0.08),
                borderRadius: '999px',
                width: '32px',
                height: '32px',
                svg: {
                  margin: '0px 6px',
                },
              },
          },
        }),
        ...(Array.isArray(sxProp) ? sxProp : sxProp ? [sxProp] : []),
      ]}
    />
  );

  if (!paginationPill) return grid;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      {grid}
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: '6px', sm: '12px' },
          right: { xs: '6px', sm: '16px' },
          left: { xs: '6px', sm: 'auto' },
          zIndex: 2,
        }}
      >
        <Pagination
          page={pillPage}
          pageCount={pillPageCount}
          pageSize={pillPageSize}
          pageSizeOptions={pillPageSizeOptions}
          rowCount={pillRowCount}
          onPageChange={(page) =>
            handlePaginationModelChange?.({ page, pageSize: pillPageSize })
          }
          onPageSizeChange={(pageSize) =>
            handlePaginationModelChange?.({ page: 0, pageSize })
          }
        />
      </Box>
    </Box>
  );
}
